import { ChangeDetectionStrategy, Component, computed, input, OnInit, signal, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { Channel, Message, User } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { MessageboardService } from 'src/app/services/messageboard.service';
import { AuthenticatedPageComponent } from '../../base.component';
import { MainpageComponent } from '../../mainpage/mainpage.component';
import { MessageBoardIndexComponent } from '../message-board-index/message-board-index.component';

@Component({
    selector: 'app-message-board',
    templateUrl: './message-board.component.html',
    styleUrls: ['./message-board.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageBoardComponent extends AuthenticatedPageComponent implements OnInit {

    public static route = 'topic';
    private static MESSAGES_PER_PAGE = 20;

    // Aliased to their route param names ('id'/'page') while keeping distinct property names,
    // since channelId/page below are plain mutable fields (channelId parsed from this input;
    // page also written by nextPage()/prevPage()) rather than something these input()s could be
    // directly.
    idParam = input<string>(undefined, { alias: 'id' });
    pageParam = input<string>(undefined, { alias: 'page' });
    // Reshaped into an {id, page} object (matching the old route.params.subscribe access pattern
    // below: channelArrAndRouteParams[1].id/.page) so the surrounding combineLatest/derivation
    // logic - including the pagination math and channel-fetch fallback branches - is untouched by
    // the route.params -> input() swap.
    private paramsLike$ = combineLatest([toObservable(this.idParam), toObservable(this.pageParam)])
        .pipe(map(([id, page]) => ({ id, page })));

    channelId: number;
    // Reassigned (to a different per-channel Signal<Channel>/derived computed()) whenever the
    // routed channel id changes, inside the async switchMap below - not one of OnPush's triggers
    // on its own, so the field itself needs to be a signal. Reading through it is a double call
    // (channelSignal()() / messagesSignal()()): the outer signal for "which channel/derivation is
    // active right now", the inner one for that channel's own live value.
    protected channelSignal = signal<Signal<Channel> | undefined>(undefined);
    protected messagesSignal = signal<Signal<Message[]> | undefined>(undefined);
    usersByUsername: Map<string, User>;
    composeMessage = '';

    page: number = 1;
    startIndex = 0;
    endIndex = MessageBoardComponent.MESSAGES_PER_PAGE - 1;
    nextPageExists = false;
    prevPageExists = false;


    constructor(public coreAppService: ApplicationService, private messageBoardService: MessageboardService, router: Router) {
        super(router, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
    }

    ngOnInit(): void {
        this.subscribe(
            combineLatest([this.messageBoardService.getPublicChannels(), this.paramsLike$])
                .pipe(
                    switchMap((channelArrAndRouteParams) => {
                        this.page = Number.parseInt(channelArrAndRouteParams[1].page ?? '1');
                        this.startIndex = ((this.page - 1) * MessageBoardComponent.MESSAGES_PER_PAGE);
                        this.endIndex = (this.page * MessageBoardComponent.MESSAGES_PER_PAGE) - 1;

                        this.channelId = Number.parseInt(channelArrAndRouteParams[1].id);
                        if (this.channelId === null || this.channelId === undefined) {
                            this.router.navigate([MessageBoardIndexComponent.route, 1]);
                            return of(<Channel>null);
                        }

                        let channelSignal = channelArrAndRouteParams[0].get(this.channelId);
                        this.channelSignal.set(channelSignal);
                        if (channelSignal) { //get messages for loaded channel
                            this.messagesSignal.set(computed(() => {
                                const channel = channelSignal();
                                this.nextPageExists = channel.Messages.length > (this.page * MessageBoardComponent.MESSAGES_PER_PAGE);
                                this.prevPageExists = this.page !== 1;
                                return channel.Messages.slice(this.startIndex, this.endIndex + 1);
                            }));
                        }

                        if (!channelSignal) { //nothing loaded yet
                            return this.messageBoardService.retrieveChannels(null, this.channelId)
                                .pipe(
                                    map(messageResponse => messageResponse.Channels?.length ?
                                        messageResponse.Channels[0] :
                                        <Channel>null));
                        } else { //fetch newest messages for page 1, then work backwards from there
                            return this.messageBoardService.getMessagesPage(this.page === 1, this.channelId, null)
                                .pipe(
                                    map(messageResponse => messageResponse.Channels?.length ? messageResponse.Channels[0] : <Channel>null));
                        }
                    })),
            (channel) => {
                if (!channel && !this.channelSignal()) { //somehow this doesn't exist, abandon ship
                    this.router.navigate([MessageBoardIndexComponent.route, 1]);
                } else {
                    // SenderProfile hydration is now handled centrally by BaseMessagingService for
                    // every message that enters a channel - see its hydrateSenderProfiles/
                    // hydrateAndUpdateChannels.
                    if (channel.ContainsNotification) {
                        setTimeout(() => this.messageBoardService.markNotificationsRead(...channel.Messages
                            .filter(m => m.targetingNotification)), 500);

                        setTimeout(() => this.messageBoardService.markMessagesRead(...channel.Messages
                            .filter(m => !m.Read)), 900);
                    }
                }
            });
    }

    sendMessage(messageText: string, replyMessage: number | null): void {
        const message = new Message(null, null);
        message.ChannelId = this.channelId;
        message.SendDate = new Date(Date.now());
        message.MessageText = messageText;
        message.IsMyMessage = true;
        if (replyMessage) {
            message.ParentMessageId = replyMessage;
        }
        this.messageBoardService.sendMessage(message);
        this.composeMessage = '';
    }


    nextPage() {
        this.page++;
        this.router.navigate([MessageBoardComponent.route, this.channelId, this.page]);
    }

    prevPage() {
        this.page--;
        this.router.navigate([MessageBoardComponent.route, this.channelId, this.page]);
    }


}
